import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  FileText, 
  Lightbulb, 
  Ruler, 
  Box, 
  Calculator, 
  Package, 
  ClipboardList, 
  HardHat, 
  Power, 
  HeartHandshake 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section, SectionTitle, FadeIn } from '../components/Section';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const stepIcons = [FileText, Lightbulb, Ruler, Box, Calculator, Package, ClipboardList, HardHat, Power, HeartHandshake];

export default function Process() {
  const { language } = useLanguage();

  const processSteps = {
    en: [
      { number: '01', title: 'Project Brief', description: 'We begin by understanding your vision, objectives, site conditions, and constraints. This foundational step ensures alignment from the start.', deliverables: ['Vision document', 'Site requirements', 'Project timeline'] },
      { number: '02', title: 'Concept Development', description: 'Our design team develops creative fountain concepts that align with your aesthetic goals and technical requirements.', deliverables: ['Concept sketches', 'Design options', 'Initial recommendations'] },
      { number: '03', title: 'Technical Design', description: 'Detailed engineering design including hydraulic calculations, structural requirements, and system specifications.', deliverables: ['Technical drawings', 'Hydraulic design', 'System specifications'] },
      { number: '04', title: '3D Simulation', description: 'Advanced 3D modeling and visualization to bring your fountain concept to life, including show choreography preview.', deliverables: ['3D renderings', 'Animation preview', 'Show simulation'] },
      { number: '05', title: 'Budget Orientation', description: 'Comprehensive cost estimation covering equipment, installation, and operational considerations.', deliverables: ['Budget estimate', 'Cost breakdown', 'Value engineering options'] },
      { number: '06', title: 'Equipment Selection', description: 'Careful selection of premium components from leading manufacturers, optimized for your specific requirements.', deliverables: ['Equipment specifications', 'Manufacturer selection', 'Procurement plan'] },
      { number: '07', title: 'Technical Documentation', description: 'Complete technical documentation package for construction, including detailed drawings and specifications.', deliverables: ['Construction drawings', 'Installation manual', 'Technical specs'] },
      { number: '08', title: 'Site Assistance', description: 'On-site technical supervision during installation to ensure proper assembly and quality control.', deliverables: ['Installation supervision', 'Quality checks', 'Progress reports'] },
      { number: '09', title: 'Commissioning', description: 'System startup, testing, calibration, and performance optimization to achieve peak operation.', deliverables: ['System testing', 'Performance tuning', 'Operational training'] },
      { number: '10', title: 'After-Sales Support', description: 'Ongoing technical support, maintenance guidance, and optimization services post-commissioning.', deliverables: ['Maintenance plan', 'Technical support', 'Performance monitoring'] },
    ],
    fr: [
      { number: '01', title: 'Brief Projet', description: 'Nous commençons par comprendre votre vision, objectifs, conditions du site et contraintes. Cette étape fondamentale assure l\'alignement dès le départ.', deliverables: ['Document de vision', 'Exigences du site', 'Calendrier projet'] },
      { number: '02', title: 'Développement du Concept', description: 'Notre équipe de design développe des concepts de fontaines créatifs alignés avec vos objectifs esthétiques et techniques.', deliverables: ['Esquisses de concept', 'Options de design', 'Recommandations initiales'] },
      { number: '03', title: 'Design Technique', description: 'Design d\'ingénierie détaillé incluant calculs hydrauliques, exigences structurelles et spécifications système.', deliverables: ['Dessins techniques', 'Design hydraulique', 'Spécifications système'] },
      { number: '04', title: 'Simulation 3D', description: 'Modélisation 3D avancée et visualisation pour donner vie à votre concept, incluant aperçu de chorégraphie de spectacle.', deliverables: ['Rendus 3D', 'Aperçu animation', 'Simulation spectacle'] },
      { number: '05', title: 'Orientation Budget', description: 'Estimation complète des coûts couvrant équipement, installation et considérations opérationnelles.', deliverables: ['Estimation budget', 'Ventilation des coûts', 'Options d\'optimisation'] },
      { number: '06', title: 'Sélection d\'Équipements', description: 'Sélection soigneuse de composants premium des fabricants leaders, optimisés pour vos besoins spécifiques.', deliverables: ['Spécifications équipement', 'Sélection fabricant', 'Plan d\'approvisionnement'] },
      { number: '07', title: 'Documentation Technique', description: 'Package complet de documentation technique pour la construction, incluant dessins détaillés et spécifications.', deliverables: ['Dessins de construction', 'Manuel d\'installation', 'Specs techniques'] },
      { number: '08', title: 'Assistance sur Site', description: 'Supervision technique sur site pendant l\'installation pour assurer un assemblage correct et contrôle qualité.', deliverables: ['Supervision installation', 'Contrôles qualité', 'Rapports d\'avancement'] },
      { number: '09', title: 'Mise en Service', description: 'Démarrage système, tests, calibration et optimisation des performances pour atteindre un fonctionnement optimal.', deliverables: ['Tests système', 'Réglage performances', 'Formation opérationnelle'] },
      { number: '10', title: 'Support Après-Vente', description: 'Support technique continu, conseils de maintenance et services d\'optimisation post-mise en service.', deliverables: ['Plan de maintenance', 'Support technique', 'Suivi des performances'] },
    ],
    es: [
      { number: '01', title: 'Brief del Proyecto', description: 'Comenzamos entendiendo su visión, objetivos, condiciones del sitio y restricciones. Este paso fundamental asegura la alineación desde el inicio.', deliverables: ['Documento de visión', 'Requisitos del sitio', 'Cronograma del proyecto'] },
      { number: '02', title: 'Desarrollo del Concepto', description: 'Nuestro equipo de diseño desarrolla conceptos creativos de fuentes alineados con sus objetivos estéticos y técnicos.', deliverables: ['Bocetos de concepto', 'Opciones de diseño', 'Recomendaciones iniciales'] },
      { number: '03', title: 'Diseño Técnico', description: 'Diseño de ingeniería detallado incluyendo cálculos hidráulicos, requisitos estructurales y especificaciones del sistema.', deliverables: ['Dibujos técnicos', 'Diseño hidráulico', 'Especificaciones del sistema'] },
      { number: '04', title: 'Simulación 3D', description: 'Modelado 3D avanzado y visualización para dar vida a su concepto, incluyendo vista previa de coreografía de show.', deliverables: ['Renderizados 3D', 'Vista previa de animación', 'Simulación de show'] },
      { number: '05', title: 'Orientación de Presupuesto', description: 'Estimación completa de costos cubriendo equipos, instalación y consideraciones operativas.', deliverables: ['Estimación de presupuesto', 'Desglose de costos', 'Opciones de optimización'] },
      { number: '06', title: 'Selección de Equipos', description: 'Selección cuidadosa de componentes premium de fabricantes líderes, optimizados para sus requisitos específicos.', deliverables: ['Especificaciones de equipos', 'Selección de fabricante', 'Plan de adquisición'] },
      { number: '07', title: 'Documentación Técnica', description: 'Paquete completo de documentación técnica para construcción, incluyendo dibujos detallados y especificaciones.', deliverables: ['Dibujos de construcción', 'Manual de instalación', 'Especificaciones técnicas'] },
      { number: '08', title: 'Asistencia en Sitio', description: 'Supervisión técnica en sitio durante la instalación para asegurar el ensamblaje correcto y control de calidad.', deliverables: ['Supervisión de instalación', 'Controles de calidad', 'Informes de progreso'] },
      { number: '09', title: 'Puesta en Marcha', description: 'Arranque del sistema, pruebas, calibración y optimización del rendimiento para alcanzar la operación óptima.', deliverables: ['Pruebas del sistema', 'Ajuste de rendimiento', 'Capacitación operativa'] },
      { number: '10', title: 'Soporte Postventa', description: 'Soporte técnico continuo, orientación de mantenimiento y servicios de optimización post-puesta en marcha.', deliverables: ['Plan de mantenimiento', 'Soporte técnico', 'Monitoreo de rendimiento'] },
    ],
  };

  const currentSteps = processSteps[language] || processSteps.en;

  const pageText = {
    en: {
      eyebrow: 'Our Process',
      title: 'From Concept to Commissioning',
      subtitle: 'A structured, transparent approach that ensures excellence at every phase of your fountain project, with clear deliverables and milestones.',
      deliverables: 'Key Deliverables',
      flexibleEyebrow: 'Flexible Engagement',
      flexibleTitle: 'Tailored to Your Needs',
      flexibleDesc: "You don't have to engage us for the full lifecycle. We adapt our services to your specific needs, whether you require support for a single phase or comprehensive project partnership.",
      designOnly: { title: 'Design Only', desc: 'Concept, technical design, and 3D simulation services for projects managed by your team.' },
      equipmentSupport: { title: 'Equipment + Support', desc: 'Premium equipment supply with installation guidance and commissioning assistance.' },
      fullTurnkey: { title: 'Full Turnkey', desc: 'Complete project management from concept to commissioning and after-sales.' },
      ctaTitle: 'Ready to Start Your Project?',
      ctaDesc: "Let's discuss where you are in your project journey and how we can support you with the right level of engagement.",
      ctaBtn: 'Start the Conversation',
    },
    fr: {
      eyebrow: 'Notre Processus',
      title: 'Du Concept à la Mise en Service',
      subtitle: 'Une approche structurée et transparente qui garantit l\'excellence à chaque phase de votre projet de fontaine, avec des livrables et jalons clairs.',
      deliverables: 'Livrables Clés',
      flexibleEyebrow: 'Engagement Flexible',
      flexibleTitle: 'Adapté à Vos Besoins',
      flexibleDesc: 'Vous n\'êtes pas obligé de nous engager pour tout le cycle de vie. Nous adaptons nos services à vos besoins spécifiques, que vous ayez besoin de support pour une seule phase ou d\'un partenariat de projet complet.',
      designOnly: { title: 'Design Uniquement', desc: 'Services de concept, design technique et simulation 3D pour les projets gérés par votre équipe.' },
      equipmentSupport: { title: 'Équipement + Support', desc: 'Fourniture d\'équipements premium avec accompagnement à l\'installation et assistance à la mise en service.' },
      fullTurnkey: { title: 'Clé en Main Complet', desc: 'Gestion de projet complète du concept à la mise en service et après-vente.' },
      ctaTitle: 'Prêt à Démarrer Votre Projet ?',
      ctaDesc: 'Discutons de où vous en êtes dans votre parcours projet et comment nous pouvons vous accompagner avec le bon niveau d\'engagement.',
      ctaBtn: 'Démarrer la Conversation',
    },
    es: {
      eyebrow: 'Nuestro Proceso',
      title: 'Del Concepto a la Puesta en Marcha',
      subtitle: 'Un enfoque estructurado y transparente que garantiza la excelencia en cada fase de su proyecto de fuente, con entregables e hitos claros.',
      deliverables: 'Entregables Clave',
      flexibleEyebrow: 'Compromiso Flexible',
      flexibleTitle: 'Adaptado a Sus Necesidades',
      flexibleDesc: 'No tiene que contratarnos para todo el ciclo de vida. Adaptamos nuestros servicios a sus necesidades específicas, ya sea que requiera soporte para una sola fase o una asociación de proyecto integral.',
      designOnly: { title: 'Solo Diseño', desc: 'Servicios de concepto, diseño técnico y simulación 3D para proyectos gestionados por su equipo.' },
      equipmentSupport: { title: 'Equipos + Soporte', desc: 'Suministro de equipos premium con orientación de instalación y asistencia de puesta en marcha.' },
      fullTurnkey: { title: 'Llave en Mano Completo', desc: 'Gestión completa del proyecto desde el concepto hasta la puesta en marcha y postventa.' },
      ctaTitle: '¿Listo para Iniciar Su Proyecto?',
      ctaDesc: 'Hablemos de dónde está en su recorrido de proyecto y cómo podemos apoyarlo con el nivel de compromiso adecuado.',
      ctaBtn: 'Iniciar la Conversación',
    },
  };

  const text = pageText[language] || pageText.en;

  return (
    <main data-testid="process-page">
      <SEO page="process" />
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 hero-gradient overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full bg-[#29ABE2]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-medium uppercase tracking-widest text-[#29ABE2] mb-4 block">
              {text.eyebrow}
            </span>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {text.title}
            </h1>
            <p className="text-xl text-[#6C757D] leading-relaxed">
              {text.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Timeline */}
      <Section background="white">
        <div className="space-y-0">
          {currentSteps.map((step, index) => {
            const Icon = stepIcons[index] || FileText;
            return (
              <FadeIn key={index} delay={index * 0.05}>
                <div className={`grid lg:grid-cols-12 gap-8 py-12 ${index !== currentSteps.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-4">
                    <span className="text-6xl font-bold text-[#29ABE2]/20">{step.number}</span>
                    <div className="w-12 h-12 bg-[#29ABE2]/10 rounded-full flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#29ABE2]" />
                    </div>
                  </div>
                  <div className="lg:col-span-6">
                    <h3 className="text-2xl font-bold text-[#1A1A1A] mb-4 uppercase tracking-wide">
                      {step.title}
                    </h3>
                    <p className="text-[#6C757D] leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  <div className="lg:col-span-4">
                    <h4 className="text-sm font-bold text-[#1A1A1A] uppercase tracking-wider mb-3">
                      {text.deliverables}
                    </h4>
                    <ul className="space-y-2">
                      {step.deliverables.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-[#6C757D]">
                          <span className="w-1.5 h-1.5 bg-[#29ABE2] rounded-full" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* Flexible Engagement */}
      <Section background="surface">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <SectionTitle
              eyebrow={text.flexibleEyebrow}
              title={text.flexibleTitle}
              description={text.flexibleDesc}
            />
            <div className="space-y-4 mt-8">
              <div className="p-4 bg-white border border-gray-100">
                <h4 className="font-bold text-[#1A1A1A] mb-1">{text.designOnly.title}</h4>
                <p className="text-sm text-[#6C757D]">{text.designOnly.desc}</p>
              </div>
              <div className="p-4 bg-white border border-gray-100">
                <h4 className="font-bold text-[#1A1A1A] mb-1">{text.equipmentSupport.title}</h4>
                <p className="text-sm text-[#6C757D]">{text.equipmentSupport.desc}</p>
              </div>
              <div className="p-4 bg-white border border-gray-100">
                <h4 className="font-bold text-[#1A1A1A] mb-1">{text.fullTurnkey.title}</h4>
                <p className="text-sm text-[#6C757D]">{text.fullTurnkey.desc}</p>
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <img
              src="https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3Mzg1NjMwMHww&ixlib=rb-4.1.0&q=85"
              alt="Project consultation"
              className="w-full aspect-square object-cover rounded-sm"
            />
          </FadeIn>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="dark" paddingY="py-20 lg:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="text-4xl lg:text-5xl font-bold text-white tracking-tight uppercase mb-6">
              {text.ctaTitle}
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              {text.ctaDesc}
            </p>
            <Link to="/contact">
              <Button
                data-testid="process-cta-btn"
                className="bg-[#29ABE2] text-white hover:bg-[#0077B6] rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
              >
                {text.ctaBtn}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
